import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Pencil, Trash2, BookOpen, Car } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const AdminCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'theorie',
    description: '',
    location: 'Frankfurt Schwanheim',
    start_date: '',
    end_date: '',
    start_time: '18:00',
    end_time: '20:00',
    active: true
  });

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/calendar`);
      setEvents(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Termine');
    } finally {
      setLoading(false);
    }
  };

  const locations = [
    'Frankfurt Schwanheim',
    'Frankfurt Niederrad',
    'Offenbach am Main',
    'Bad Homburg'
  ];

  const resetForm = () => {
    setFormData({
      title: '',
      event_type: 'theorie',
      description: '',
      location: 'Frankfurt Schwanheim',
      start_date: '',
      end_date: '',
      start_time: '18:00',
      end_time: '20:00',
      active: true
    });
    setEditingEvent(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      event_type: event.event_type,
      description: event.description || '',
      location: event.location,
      start_date: event.start_date,
      end_date: event.end_date || '',
      start_time: event.start_time,
      end_time: event.end_time,
      active: event.active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_date || !formData.start_time || !formData.end_time) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    try {
      if (editingEvent) {
        await axios.put(`${API}/calendar/${editingEvent.id}`, formData, { headers });
        toast.success('Termin aktualisiert!');
      } else {
        await axios.post(`${API}/calendar`, formData, { headers });
        toast.success('Termin erstellt!');
      }
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Termin wirklich löschen?')) return;
    
    try {
      await axios.delete(`${API}/calendar/${id}`, { headers });
      toast.success('Termin gelöscht!');
      fetchEvents();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getEventTypeBadge = (type) => {
    switch (type) {
      case 'theorie':
        return { label: 'Theorie', color: 'bg-blue-100 text-blue-800', icon: BookOpen };
      case 'intensiv':
        return { label: 'Intensivkurs', color: 'bg-orange-100 text-orange-800', icon: Car };
      default:
        return { label: type, color: 'bg-gray-100 text-gray-800', icon: Calendar };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Unterrichtskalender</h1>
          <p className="text-slate-500">Verwalten Sie Theorie- und Intensivkurstermine</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-500">
          <Plus className="w-4 h-4 mr-2" />
          Neuer Termin
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Alle Termine ({events.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {events.length > 0 ? (
              <div className="space-y-4">
                {events
                  .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                  .map(event => {
                    const badge = getEventTypeBadge(event.event_type);
                    const IconComponent = badge.icon;
                    
                    return (
                      <div
                        key={event.id}
                        className={`p-4 rounded-lg border bg-white flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                          !event.active ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={badge.color}>
                              <IconComponent className="w-3 h-3 mr-1" />
                              {badge.label}
                            </Badge>
                            {!event.active && (
                              <Badge variant="outline" className="text-slate-500">Inaktiv</Badge>
                            )}
                          </div>
                          <h4 className="font-semibold text-slate-900">{event.title}</h4>
                          <div className="text-sm text-slate-500 mt-1">
                            <span>
                              {format(parseISO(event.start_date), 'dd.MM.yyyy', { locale: de })}
                              {event.end_date && event.end_date !== event.start_date && (
                                <> - {format(parseISO(event.end_date), 'dd.MM.yyyy', { locale: de })}</>
                              )}
                            </span>
                            <span className="mx-2">|</span>
                            <span>{event.start_time} - {event.end_time} Uhr</span>
                            <span className="mx-2">|</span>
                            <span>{event.location}</span>
                          </div>
                          {event.description && (
                            <p className="text-sm text-slate-400 mt-1">{event.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openEditDialog(event)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(event.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Noch keine Termine vorhanden</p>
                <Button onClick={openCreateDialog} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Ersten Termin erstellen
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Termin bearbeiten' : 'Neuer Termin'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="z.B. Theorieunterricht Lektion 1-4"
                  required
                />
              </div>

              <div>
                <Label htmlFor="event_type">Typ *</Label>
                <Select
                  value={formData.event_type}
                  onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="theorie">Theorieunterricht</SelectItem>
                    <SelectItem value="intensiv">Intensivkurs</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="location">Standort *</Label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData({ ...formData, location: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start_date">Startdatum *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="end_date">Enddatum (für mehrtägige Kurse)</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="start_time">Startzeit *</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="end_time">Endzeit *</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="description">Beschreibung</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optionale Beschreibung oder Hinweise"
                  rows={3}
                />
              </div>

              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="active" className="font-normal">Termin ist aktiv und sichtbar</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500">
                {editingEvent ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendar;
